import { useState } from "react";

import { useLocale, useT } from "../../shared/i18n";
import type { ApiClient } from "../../shared/api/client";
import { Banner, Button } from "../../shared/ui";
import { fill, formatDate } from "./accountFormat";

/**
 * Replaces the whole shell while a workspace waits to be purged. The only
 * primary action is to restore it; revoking export links is the only other
 * call the server accepts from a restricted session.
 */
export function PendingDeletion({
  api,
  purgeAt,
  onRestored,
}: {
  api: Pick<ApiClient, "cancelDeletion" | "revokeAllExports">;
  /** Null while the date is still being fetched or could not be read. */
  purgeAt: string | null;
  /** Called after a successful restore; the app starts over from the session. */
  onRestored: () => void;
}) {
  const t = useT();
  const { locale } = useLocale();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const restore = async () => {
    setBusy(true);
    setFailed(false);
    try {
      await api.cancelDeletion();
      onRestored();
    } catch {
      setFailed(true);
      setBusy(false);
    }
  };
  const [revoke, setRevoke] = useState<"idle" | "busy" | "done" | "failed">(
    "idle",
  );
  const revokeLinks = async () => {
    setRevoke("busy");
    try {
      await api.revokeAllExports();
      setRevoke("done");
    } catch {
      setRevoke("failed");
    }
  };
  return (
    <main
      aria-labelledby="pending-deletion-title"
      className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center gap-4 bg-canvas p-6 text-fg"
    >
      <h1 id="pending-deletion-title" className="text-title text-fg">
        {t("account.pending.title")}
      </h1>
      <p className="text-body text-fg-muted">
        {purgeAt
          ? fill(t("account.pending.body"), {
              when: formatDate(locale, purgeAt),
            })
          : t("account.pending.bodyUnknown")}
      </p>
      {failed && <Banner tone="error">{t("account.pending.failed")}</Banner>}
      <Button
        disabled={busy}
        loading={busy}
        onClick={() => {
          void restore();
        }}
      >
        {t("account.pending.restore")}
      </Button>
      {revoke === "done" && (
        <Banner tone="success">{t("account.pending.revokeDone")}</Banner>
      )}
      {revoke === "failed" && (
        <Banner tone="error">{t("account.pending.revokeFailed")}</Banner>
      )}
      <Button
        variant="secondary"
        disabled={revoke === "busy" || busy}
        loading={revoke === "busy"}
        onClick={() => {
          void revokeLinks();
        }}
      >
        {t("account.pending.revokeLinks")}
      </Button>
    </main>
  );
}
